import {
  ChakraProvider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  theme,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import {
  FiArrowRight,
  FiBookOpen,
  FiCheck,
  FiCopy,
  FiGrid,
  FiHelpCircle,
  FiLink,
  FiPlus,
  FiRefreshCw,
  FiShield,
  FiSliders,
} from "react-icons/fi";
import { QrPanel } from "./components/QrPanel";
import {
  EMPTY_UTM,
  MAX_BATCH,
  parseLinks,
  shortenLink,
  UtmValues,
  UTM_FIELDS,
  withUtm,
} from "./utils/links";
import { normalizeUrl } from "./utils/normalizeUrl";
import "./App.css";

type Result = {
  id: number;
  original: string;
  short?: string;
  status: "waiting" | "working" | "success" | "error";
  error?: string;
};

export function App() {
  const permanent = window.location.pathname.replace(/\/+$/, "") === "/perm";
  const [tool, setTool] = useState<"link" | "qr">(
    new URLSearchParams(window.location.search).get("tool") === "qr"
      ? "qr"
      : "link",
  );
  const [bulk, setBulk] = useState(false);
  const [singleInput, setSingleInput] = useState("");
  const [bulkInput, setBulkInput] = useState("");
  const [utmEnabled, setUtmEnabled] = useState(false);
  const [utm, setUtm] = useState<UtmValues>({ ...EMPTY_UTM });
  const [results, setResults] = useState<Result[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [copied, setCopied] = useState("");
  const [help, setHelp] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [qrInput, setQrInput] = useState("");
  const [standaloneQr, setStandaloneQr] = useState("");
  const [qrError, setQrError] = useState("");
  const nextId = useRef(0);
  const running = useRef(false);
  const stop = useRef(false);
  const alive = useRef(true);
  const input = permanent && bulk ? bulkInput : singleInput;
  const rows =
    permanent && bulk
      ? parseLinks(input)
      : input.trim()
        ? [{ line: 1, value: input.trim() }]
        : [];
  const activeUtm = permanent && utmEnabled ? utm : undefined;
  const successful = results.filter((row) => row.status === "success");
  const retryable = results.filter(
    (row) => row.status === "error" || row.status === "waiting",
  );
  const chosen = successful.filter((row) => selected.includes(row.id));

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      stop.current = true;
    };
  }, []);
  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(""), 2200);
    return () => window.clearTimeout(timeout);
  }, [copied]);
  useEffect(() => {
    document.title = permanent
      ? "영구 링크 만들기 | Weplet Link Creator"
      : "Weplet Link Creator | URL 단축";
    const canonical = `https://create-link.weplet.com${permanent ? "/perm" : "/"}`;
    const link = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    const og = document.querySelector<HTMLMetaElement>(
      'meta[property="og:url"]',
    );
    if (link) link.href = canonical;
    if (og) og.content = canonical;
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = permanent
      ? "noindex, nofollow, noarchive, nosnippet, noimageindex"
      : "index, follow";
  }, [permanent]);

  async function copy(text: string, key: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setNotice("클립보드에 복사했습니다.");
    } catch {
      setNotice(
        "복사하지 못했습니다. 브라우저의 클립보드 권한을 확인하거나 주소를 직접 선택해 복사해주세요.",
      );
    }
  }

  async function run(queue: Result[]) {
    if (running.current) return;
    running.current = true;
    stop.current = false;
    setBusy(true);
    setStopping(false);
    setError("");
    let completed = 0;
    let failed = 0;
    for (const row of queue) {
      if (stop.current || !alive.current) break;
      setResults((previous) =>
        previous.map((item) =>
          item.id === row.id
            ? { ...item, status: "working", error: undefined }
            : item,
        ),
      );
      try {
        const short = await shortenLink(row.original);
        if (alive.current) {
          setResults((previous) =>
            previous.map((item) =>
              item.id === row.id
                ? { ...item, short, status: "success", error: undefined }
                : item,
            ),
          );
          setSelected((previous) => [...previous, row.id]);
        }
        completed++;
      } catch (err) {
        failed++;
        if (alive.current)
          setResults((previous) =>
            previous.map((item) =>
              item.id === row.id
                ? {
                    ...item,
                    status: "error",
                    error:
                      err instanceof Error
                        ? err.message
                        : "요청에 실패했습니다. 다시 시도해주세요.",
                  }
                : item,
            ),
          );
      }
    }
    running.current = false;
    if (alive.current) {
      setBusy(false);
      setStopping(false);
      setNotice(
        `${stop.current ? "생성을 중지했습니다. " : ""}${completed}개 생성 완료${failed ? `, ${failed}개 실패` : ""}.`,
      );
    }
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (running.current) return;
    setError("");
    if (!rows.length) {
      setError("단축할 주소를 입력해주세요.");
      return;
    }
    if (rows.length > MAX_BATCH) {
      setError(`한 번에 최대 ${MAX_BATCH}개까지 생성할 수 있습니다.`);
      return;
    }
    const invalid = rows.filter((row) => !normalizeUrl(row.value));
    if (invalid.length) {
      setError(
        `${invalid.map((row) => row.line).join(", ")}번째 줄의 주소를 확인해주세요. example.com 또는 https://example.com 형식으로 입력해주세요.`,
      );
      return;
    }
    if (
      activeUtm &&
      UTM_FIELDS.some((field) => field.required && !activeUtm[field.key].trim())
    ) {
      setError("UTM의 유입 출처, 유입 매체, 캠페인 이름을 모두 입력해주세요.");
      return;
    }
    const queue: Result[] = rows.map((row) => ({
      id: ++nextId.current,
      original: withUtm(row.value, activeUtm)!,
      status: "waiting",
    }));
    setResults((previous) => [...previous, ...queue]);
    setNotice(`${queue.length}개 링크를 순서대로 생성합니다.`);
    if (bulk) setBulkInput("");
    else setSingleInput("");
    void run(queue);
  }

  return (
    <ChakraProvider theme={theme}>
      <div className="app-shell">
        <a className="skip-link" href="#workspace">
          본문으로 이동
        </a>
        <div className="page-container">
          <header className="site-header">
            <a
              className="brand"
              href={permanent ? "/perm" : "/"}
              aria-label="Weplet Link Creator 홈"
            >
              <span className="brand-icon">
                <FiLink aria-hidden="true" />
              </span>
              <span>
                weplet<span className="brand-divider">/</span>
                <span className="brand-product">links</span>
              </span>
            </a>
            <div className="header-actions">
              {permanent ? (
                <span className="private-badge">
                  <FiShield aria-hidden="true" /> 비공개 작업 공간
                </span>
              ) : (
                <a
                  className="button ghost public-guide-link"
                  href="/guide"
                  aria-label="사용 가이드"
                >
                  <FiBookOpen aria-hidden="true" /> <span>사용 가이드</span>
                </a>
              )}
              <button className="button ghost" onClick={() => setHelp(true)}>
                <FiHelpCircle aria-hidden="true" /> 도움말
              </button>
            </div>
          </header>
          <main id="workspace">
            <section className="intro enter">
              <h1>
                {permanent ? "영구 링크 만들기" : "긴 주소를 짧고 간결하게."}
              </h1>
              <p>
                {permanent
                  ? "캠페인을 담은 링크부터 QR 코드까지, 한곳에서 만들고 공유하세요."
                  : "링크는 짧게, 공유는 가볍게. 단축 링크와 QR 코드를 만들어보세요."}
              </p>
            </section>
            <div className="workspace-grid enter delay-one">
              <section className="editor glass" aria-label="링크 도구">
                <div className="tool-tabs" role="group" aria-label="도구 선택">
                  <button
                    className={tool === "link" ? "tool-tab active" : "tool-tab"}
                    aria-pressed={tool === "link"}
                    disabled={busy}
                    onClick={() => setTool("link")}
                  >
                    <FiLink aria-hidden="true" /> 링크 만들기
                  </button>
                  <button
                    className={tool === "qr" ? "tool-tab active" : "tool-tab"}
                    aria-pressed={tool === "qr"}
                    disabled={busy}
                    onClick={() => setTool("qr")}
                  >
                    <FiGrid aria-hidden="true" /> QR 코드
                  </button>
                </div>
                {tool === "link" ? (
                  <form onSubmit={submit} noValidate>
                    <fieldset disabled={busy}>
                      <div className="section-heading">
                        <h2>어떤 링크를 줄일까요?</h2>
                        {permanent ? (
                          <div
                            className="segmented"
                            role="group"
                            aria-label="입력 방식"
                          >
                            <button
                              type="button"
                              aria-pressed={!bulk}
                              className={!bulk ? "active" : ""}
                              onClick={() => {
                                setBulk(false);
                                setError("");
                              }}
                            >
                              하나씩
                            </button>
                            <button
                              type="button"
                              aria-pressed={bulk}
                              className={bulk ? "active" : ""}
                              onClick={() => {
                                setBulk(true);
                                setError("");
                              }}
                            >
                              여러 개
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <label className="field-label" htmlFor="url">
                        {bulk && permanent ? "원본 URL 목록" : "원본 URL"}
                      </label>
                      {bulk && permanent ? (
                        <textarea
                          id="url"
                          rows={5}
                          placeholder={
                            "example.com/first\nexample.com/second\nexample.com/third"
                          }
                          value={bulkInput}
                          onChange={(e) => setBulkInput(e.target.value)}
                          spellCheck={false}
                          aria-describedby="url-hint"
                        />
                      ) : (
                        <input
                          id="url"
                          type="text"
                          inputMode="url"
                          autoCapitalize="none"
                          autoCorrect="off"
                          placeholder="example.com 또는 https://example.com"
                          value={singleInput}
                          onChange={(e) => setSingleInput(e.target.value)}
                          spellCheck={false}
                          aria-describedby="url-hint"
                        />
                      )}
                      <div className="field-meta" id="url-hint">
                        <span>
                          {bulk && permanent
                            ? `한 줄에 주소 하나씩 · 최대 ${MAX_BATCH}개`
                            : "https://는 생략해도 괜찮아요."}
                        </span>
                        {bulk && permanent ? (
                          <span>{rows.length}개 입력됨</span>
                        ) : null}
                      </div>
                      {permanent ? (
                        <div className="utm-section">
                          <label className="utm-toggle" htmlFor="utm-toggle">
                            <span className="utm-title">
                              <FiSliders aria-hidden="true" />
                              <span>
                                UTM 설정
                                <span className="muted small">
                                  링크에 캠페인 정보 추가
                                </span>
                              </span>
                            </span>
                            <input
                              id="utm-toggle"
                              className="switch-input"
                              type="checkbox"
                              role="switch"
                              checked={utmEnabled}
                              onChange={(e) => setUtmEnabled(e.target.checked)}
                            />
                          </label>
                          {utmEnabled ? (
                            <div className="utm-fields">
                              <p className="small muted">
                                {bulk
                                  ? "입력한 모든 링크에 같은 UTM을 적용합니다. "
                                  : ""}
                                필수 3개 항목을 입력해주세요. 입력한 값은 기존의
                                같은 UTM 값을 대체하며, 비워둔 선택 항목은
                                유지됩니다.
                              </p>
                              <div className="fields-grid">
                                {UTM_FIELDS.map((field) => (
                                  <div
                                    className={
                                      field.key === "utm_campaign"
                                        ? "field full"
                                        : "field"
                                    }
                                    key={field.key}
                                  >
                                    <label
                                      className="field-label"
                                      htmlFor={field.key}
                                    >
                                      {field.label}{" "}
                                      <span className="optional">
                                        {field.required ? "필수" : "선택"}
                                      </span>
                                    </label>
                                    <input
                                      id={field.key}
                                      value={utm[field.key]}
                                      onChange={(e) =>
                                        setUtm((previous) => ({
                                          ...previous,
                                          [field.key]: e.target.value,
                                        }))
                                      }
                                      placeholder={field.example}
                                      required={field.required}
                                      spellCheck={false}
                                    />
                                    <span className="field-key">
                                      {field.key}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {rows.length ? (
                        <details className="url-preview">
                          <summary>
                            최종 주소 미리보기 <span>{rows.length}개</span>
                          </summary>
                          <ol>
                            {rows.slice(0, MAX_BATCH).map((row) => (
                              <li key={row.line}>
                                <span>{row.line}.</span>
                                <code>
                                  {withUtm(row.value, activeUtm) ||
                                    "올바른 주소를 입력해주세요."}
                                </code>
                              </li>
                            ))}
                          </ol>
                        </details>
                      ) : null}
                    </fieldset>
                    {error ? (
                      <p role="alert" className="error-message">
                        {error}
                      </p>
                    ) : null}
                    <div className="submit-row">
                      <span className="small muted">
                        {busy
                          ? "한 번에 하나씩 생성하고 있어요."
                          : "link.weplet.com 단축 URL을 생성합니다."}
                      </span>
                      <button
                        className="button primary"
                        type="submit"
                        disabled={busy || !rows.length}
                      >
                        {busy ? (
                          <>
                            <span className="spinner" /> 생성 중
                          </>
                        ) : (
                          <>
                            {bulk && permanent
                              ? rows.length
                                ? `${rows.length}개 링크 만들기`
                                : "여러 개 링크 만들기"
                              : "링크 만들기"}
                            <FiArrowRight aria-hidden="true" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="standalone-qr">
                    <h2>스캔 한 번으로 연결하세요.</h2>
                    <p className="muted">
                      원본 주소로 바로 QR 코드를 만들 수 있어요.
                    </p>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const url = normalizeUrl(qrInput);
                        if (!url) {
                          setQrError("올바른 웹 주소를 입력해주세요.");
                          return;
                        }
                        setQrError("");
                        setStandaloneQr(url);
                      }}
                      noValidate
                    >
                      <label className="field-label" htmlFor="qr-input">
                        QR 코드에 담을 URL
                      </label>
                      <input
                        id="qr-input"
                        placeholder="example.com"
                        value={qrInput}
                        onChange={(e) => {
                          setQrInput(e.target.value);
                          setStandaloneQr("");
                          setQrError("");
                        }}
                        inputMode="url"
                        spellCheck={false}
                      />
                      {qrError ? (
                        <p role="alert" className="error-message">
                          {qrError}
                        </p>
                      ) : null}
                      <button
                        className="button primary qr-submit"
                        type="submit"
                        disabled={!qrInput.trim()}
                      >
                        <FiGrid aria-hidden="true" /> QR 코드 만들기
                      </button>
                    </form>
                    <QrPanel url={standaloneQr} />
                  </div>
                )}
              </section>
              <aside className="guide" aria-label="사용 안내">
                <span className="guide-icon">
                  <FiLink aria-hidden="true" />
                </span>
                <h2>
                  만들고, 복사하고,
                  <br />
                  바로 공유하세요.
                </h2>
                <p>
                  주소 하나로 시작해서
                  <br />
                  필요한 형태로 나눠보세요.
                </p>
                <ol className="guide-steps">
                  <li>
                    <span>01</span>
                    <div>
                      <strong>주소 입력</strong>
                      <p>
                        {permanent
                          ? "하나씩 또는 여러 개를 한 번에"
                          : "https:// 없이도 간편하게"}
                      </p>
                    </div>
                  </li>
                  <li>
                    <span>02</span>
                    <div>
                      <strong>
                        {permanent ? "캠페인 정보 설정" : "단축 링크 생성"}
                      </strong>
                      <p>
                        {permanent
                          ? "필요할 때만 UTM을 추가해요"
                          : "짧은 주소로 깔끔하게 정리해요"}
                      </p>
                    </div>
                  </li>
                  <li>
                    <span>03</span>
                    <div>
                      <strong>원하는 방식으로 공유</strong>
                      <p>링크를 복사하거나 QR로 저장해요</p>
                    </div>
                  </li>
                </ol>
                <div className="guide-note">
                  <FiShield aria-hidden="true" />
                  <p>
                    {permanent
                      ? "비공개 운영 페이지입니다. 이 페이지의 URL을 외부에 공유하지 마세요."
                      : "생성한 링크는 자동으로 만료되거나 삭제되지 않습니다."}
                  </p>
                </div>
              </aside>
            </div>
            <section
              className="results-section glass enter delay-two"
              aria-labelledby="results-title"
              aria-busy={busy}
            >
              <div className="results-heading">
                <div>
                  <h2 id="results-title">
                    생성된 링크{" "}
                    <span className="count">{successful.length}</span>
                  </h2>
                  <p className="small muted">
                    {results.length
                      ? "이 페이지에서 생성한 링크입니다. 페이지를 새로고침하면 목록은 사라집니다."
                      : "링크를 만들면 이곳에 차곡차곡 모여요."}
                  </p>
                </div>
                {results.length && !busy ? (
                  <button
                    className="button ghost"
                    onClick={() => {
                      setResults([]);
                      setSelected([]);
                      setNotice(
                        "결과 목록을 비웠습니다. 생성한 링크는 유지됩니다.",
                      );
                    }}
                  >
                    목록 비우기
                  </button>
                ) : null}
              </div>
              {results.length ? (
                <>
                  <div className="results-toolbar">
                    {permanent ? (
                      <div className="selection-tools">
                        <label className="select-all">
                          <input
                            type="checkbox"
                            checked={
                              successful.length > 0 &&
                              chosen.length === successful.length
                            }
                            disabled={!successful.length}
                            onChange={(e) =>
                              setSelected(
                                e.target.checked
                                  ? successful.map((row) => row.id)
                                  : [],
                              )
                            }
                          />{" "}
                          전체 선택
                        </label>
                        <button
                          className="button secondary"
                          disabled={!chosen.length}
                          onClick={() =>
                            copy(
                              chosen.map((row) => row.short).join("\n"),
                              "selected",
                            )
                          }
                        >
                          <FiCopy aria-hidden="true" />
                          {copied === "selected"
                            ? "복사됨"
                            : `선택 복사 (${chosen.length})`}
                        </button>
                        <button
                          className="button ghost"
                          disabled={!successful.length}
                          onClick={() =>
                            copy(
                              successful.map((row) => row.short).join("\n"),
                              "all",
                            )
                          }
                        >
                          {copied === "all" ? "복사됨" : "전체 복사"}
                        </button>
                      </div>
                    ) : (
                      <span className="small muted">
                        단축 링크를 복사하거나 QR 코드로 저장하세요.
                      </span>
                    )}
                    {busy ? (
                      <button
                        className="button ghost"
                        disabled={stopping}
                        onClick={() => {
                          stop.current = true;
                          setStopping(true);
                        }}
                      >
                        {stopping ? "현재 요청 완료 후 중지" : "생성 중지"}
                      </button>
                    ) : retryable.length ? (
                      <button
                        className="button ghost"
                        onClick={() => void run(retryable)}
                      >
                        <FiRefreshCw aria-hidden="true" /> 실패·대기{" "}
                        {retryable.length}개 재시도
                      </button>
                    ) : null}
                  </div>
                  {busy ? (
                    <div className="batch-progress" role="status">
                      <span className="spinner" />
                      {
                        results.filter(
                          (row) =>
                            row.status === "success" || row.status === "error",
                        ).length
                      }{" "}
                      / {results.length}개 처리됨
                      {stopping
                        ? " · 현재 요청이 끝나면 중지합니다."
                        : " · 순서대로 생성 중"}
                    </div>
                  ) : null}
                  <ul className="result-list">
                    {results.map((row, i) => (
                      <li className="result-row" key={row.id}>
                        {permanent ? (
                          <input
                            type="checkbox"
                            aria-label={`${i + 1}번 링크 선택`}
                            disabled={row.status !== "success"}
                            checked={selected.includes(row.id)}
                            onChange={(e) =>
                              setSelected((previous) =>
                                e.target.checked
                                  ? [...previous, row.id]
                                  : previous.filter((id) => id !== row.id),
                              )
                            }
                          />
                        ) : (
                          <span className="result-icon">
                            <FiLink aria-hidden="true" />
                          </span>
                        )}
                        <div className="result-content">
                          {row.short ? (
                            <a
                              className="short-url"
                              href={row.short}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {row.short}
                            </a>
                          ) : (
                            <span
                              className={
                                row.status === "error"
                                  ? "failed-status"
                                  : "muted"
                              }
                            >
                              {row.status === "working"
                                ? "링크 생성 중…"
                                : row.status === "error"
                                  ? "생성 실패"
                                  : "생성 대기"}
                            </span>
                          )}
                          <p className="original-url">{row.original}</p>
                          {row.error ? (
                            <p className="error-message">{row.error}</p>
                          ) : null}
                        </div>
                        {row.short ? (
                          <div className="row-actions">
                            <button
                              className="button secondary"
                              aria-label={`${i + 1}번 링크 복사`}
                              onClick={() => copy(row.short!, String(row.id))}
                            >
                              {copied === String(row.id) ? (
                                <FiCheck aria-hidden="true" />
                              ) : (
                                <FiCopy aria-hidden="true" />
                              )}
                              {copied === String(row.id) ? "복사됨" : "복사"}
                            </button>
                            <button
                              className="button ghost"
                              aria-label={`${i + 1}번 링크 QR 코드`}
                              onClick={() => setQrUrl(row.short!)}
                            >
                              <FiGrid aria-hidden="true" /> QR
                            </button>
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="empty-state">
                  <span className="empty-icon">
                    <FiPlus aria-hidden="true" />
                  </span>
                  <div>
                    <strong>첫 번째 링크를 만들어보세요.</strong>
                    <p>
                      생성한 링크의 복사와 QR 다운로드를 여기에서 할 수 있어요.
                    </p>
                  </div>
                </div>
              )}
            </section>
            <div className="notice" role="status" aria-live="polite">
              {notice}
            </div>
          </main>
          <footer className="site-footer">
            <span>Weplet Link Creator</span>
            <span>작은 링크로 더 편한 연결.</span>
          </footer>
        </div>
        <Modal isOpen={help} onClose={() => setHelp(false)} isCentered>
          <ModalOverlay backdropFilter="blur(6px)" />
          <ModalContent className="app-modal">
            <ModalHeader>도움말</ModalHeader>
            <ModalCloseButton aria-label="도움말 닫기" />
            <ModalBody pb={7}>
              <div className="help-content">
                <p>
                  원본 URL을 입력하고 ‘링크 만들기’를 누르면 단축 주소가
                  만들어집니다. 생성한 링크는 자동으로 만료되거나 삭제되지
                  않습니다.
                </p>
                {permanent ? (
                  <>
                    <p>
                      ‘여러 개’에서는 한 줄에 주소 하나씩, 최대 50개를
                      입력하세요. 실제 요청은 순차 처리되며, 실패한 링크가
                      있어도 나머지는 계속 생성됩니다.
                    </p>
                    <p>
                      UTM은 방문 출처를 분석하는 데 쓰는 주소 정보입니다. 유입
                      출처·매체·캠페인을 입력하면 원본 URL에 추가됩니다. 여러
                      링크에는 공통으로 적용하며, 콘텐츠와 검색어는 선택
                      사항입니다.
                    </p>
                    <p>
                      ‘생성 중지’는 진행 중인 요청이 끝난 뒤 멈춥니다. 재시도는
                      실패·대기 항목만 처리합니다. 응답 지연이나 연결 오류가
                      있었다면 중복 생성 여부를 확인해주세요.
                    </p>
                    <p>
                      선택 복사와 전체 복사는 단축 주소를 한 줄에 하나씩
                      복사합니다.
                    </p>
                  </>
                ) : null}
                <p>
                  QR 코드 탭에서는 원본 주소를, 생성 결과의 QR 버튼에서는 단축
                  주소를 PNG로 저장할 수 있습니다.
                </p>
                <p>
                  생성 결과 목록은 새로고침하면 사라집니다. 필요한 링크를 먼저
                  복사해주세요.
                </p>
                {!permanent ? (
                  <a className="button secondary help-guide-link" href="/guide">
                    자세한 사용 가이드 보기 <FiArrowRight aria-hidden="true" />
                  </a>
                ) : null}
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
        <Modal isOpen={!!qrUrl} onClose={() => setQrUrl("")} isCentered>
          <ModalOverlay backdropFilter="blur(6px)" />
          <ModalContent className="app-modal">
            <ModalHeader>링크 QR 코드</ModalHeader>
            <ModalCloseButton aria-label="QR 코드 닫기" />
            <ModalBody pb={7}>
              {qrUrl ? <QrPanel url={qrUrl} /> : null}
            </ModalBody>
          </ModalContent>
        </Modal>
      </div>
    </ChakraProvider>
  );
}
export default App;
